// SF3 — the verdict for a term, with the evidence under it.
// The leak names the stage; the shares beside it are why.
import React from 'react';
import s from '../SearchTerms.module.css';
import { money, num, pct } from '../format';

const LEAK = {
  visibility: ['Barely visible', 'Amazon rarely shows this listing for the term. The work is on relevance, not on the tile.'],
  attraction: ['Seen, not clicked', 'Shoppers see the listing and scroll past. That is the image, the title or the price on the tile.'],
  page:       ['Clicked, not added', 'The tile wins the click and the page loses the shopper. Content, images or price.'],
  checkout:   ['Added, not bought', 'The cart is reached and abandoned. Usually price or delivery.'],
  holds:      ['No leak', 'Share holds or grows through every reported stage.'],
};

export default function Diagnosis({ term }) {
  const [title, explain] = LEAK[term.leak] || ['Undetermined', term.leak_detail || 'Not enough of the funnel was reported.'];
  const tone = term.leak === 'holds' ? s.badgeCompleted
    : term.leak === 'visibility' ? s.badgePartial
    : term.leak ? s.badgeFailed : s.badgeIdle;

  return (
    <div className={s.section}>
      <div className={s.sectionTitle}>Where this term is lost</div>
      <p style={{ margin: '0 0 8px' }}>
        <span className={tone}>{title}</span>
      </p>
      <p className={s.hint}>{explain}</p>
      {term.leak_detail && term.leak !== 'holds' && (
        <p className={s.hint} style={{ marginTop: 4 }}>Evidence: {term.leak_detail}.</p>
      )}

      <div className={s.funnel} style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginTop: 14 }}>
        <div className={s.funnelStage}>
          <div className={s.funnelLabel}>Your price vs the term</div>
          {term.price ? (
            <>
              <div className={s.funnelOwn}>{money(term.price.own, 'INR')}</div>
              <div className={s.funnelShare}>
                {term.price.position === 'at' ? 'in line with' : `${term.price.position} the`} market
                {' '}({term.price.ratio.toFixed(2)}×)
              </div>
              <div className={s.funnelMarket}>term converts at {money(term.price.market, 'INR')}</div>
            </>
          ) : (
            <>
              <div className={s.funnelOwn}>—</div>
              <div className={s.funnelMarket}>no purchases on this term, so it has no converting price</div>
            </>
          )}
        </div>

        <div className={s.funnelStage}>
          <div className={s.funnelLabel}>Does fast delivery decide it</div>
          {term.delivery ? (
            <>
              <div className={s.funnelOwn}>
                <span className={term.delivery.rankable ? undefined : s.muted}>
                  {term.delivery.lift_pp > 0 ? '+' : ''}{term.delivery.lift_pp.toFixed(1)} pp
                </span>
              </div>
              <div className={s.funnelShare}>
                {pct(term.delivery.fast_share_of_purchases)} of purchases vs{' '}
                {pct(term.delivery.fast_share_of_clicks)} of clicks
              </div>
              <div className={s.funnelMarket}>
                {!term.delivery.rankable
                  ? `only ${num(term.delivery.market_purchases)} purchase${
                      term.delivery.market_purchases === 1 ? '' : 's'} behind this — too few to read`
                  : term.delivery.lift_pp >= 5 ? 'fast delivery converts better on this term'
                  : term.delivery.lift_pp <= -5 ? 'fast delivery is not what wins this term'
                  : 'delivery speed makes little difference here'}
              </div>
            </>
          ) : (
            <>
              <div className={s.funnelOwn}>—</div>
              <div className={s.funnelMarket}>
                market-wide only, and this term has too few purchases to read
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
